import type { ErasPage } from "../hud/panels";
import { decayRequested } from "../session/params";
import type { SagaEra, WitnessSource } from "../world/types";

/*
 * The Book of Eras, as the client reads it.
 *
 * Phase 6 plan, wave 2C — *"the book opens the game"* — over decision O6: the
 * saga is a **projection**, derived on demand and never stored, and every line
 * of it carries the records it narrates. That pairing is the whole contract:
 * "nothing invented" is a thing the trace view can show rather than a thing a
 * doc comment asserts, so a line that cites nothing is a line this client will
 * not put on a page ({@link readSaga} refuses it and says how many).
 *
 * **This is the only module that talks to the saga surface**, which is a
 * decision rather than tidiness. What a saga *is* belongs to the boundary and is
 * declared there — {@link SagaEra}, `SagaLine` and `WitnessSource.saga` in
 * `world/types.ts`, decoded in `world/wasm.ts` — so this file imports those
 * shapes rather than restating them, and two things stay on this side of the
 * seam:
 *
 * - The export is **presence-checked rather than assumed**. A build whose
 *   boundary has not grown it opens no book and says nothing, which is the same
 *   answer this client already gives to a world with no history in it at all
 *   (`?bootstrap=0`, `world/wasm.ts`).
 * - A line with no record behind it is **refused and counted**, whatever the
 *   declaration promises: the decode hands one over when a chapter's citation
 *   groups do not line up (`world/wasm.ts`'s `chapter`), and a page the log
 *   cannot account for is the one thing the saga is not allowed to be.
 *
 * Nothing here composes a sentence about the world. The ages, their two names
 * and their lines are the simulation's own strings; what the client decides is
 * which of the two names leads a page ({@link ERA_HEADING}) and how many pages
 * there are.
 */

/** The whole book, and what the client would not print. */
export interface SagaBook {
  /** The ages, oldest first. Empty in a build with no saga export, and in a world with no past. */
  readonly eras: readonly SagaEra[];

  /**
   * Lines the client refused because they cited nothing.
   *
   * **Non-zero is a defect, not a taste** — it means the projection composed a
   * sentence the log cannot account for, which is the one thing the saga is not
   * allowed to do (O6). Counted rather than thrown so a session still opens, and
   * asserted on in `dev/selfcheck.ts`'s `checkSaga`.
   */
  readonly refused: number;
}

/** A book with nothing in it: what a build without the export reads. */
export const EMPTY_SAGA: SagaBook = { eras: [], refused: 0 };

/**
 * Which of an age's two names a page leads with. **An open owner taste call.**
 *
 * The Phase 6 plan leaves era naming voice deliberately unowned — *"do eras get
 * generated names ('the years after the fall of Oddtorhaven') or ordinals"* —
 * and `sim-core` composes **both** surfaces precisely so the choice can be made
 * by looking at it (`saga::Era::name`, `saga::Era::title`). Until the owner
 * rules, a page carries both: the generated name as the heading, the ordinal as
 * the line under it.
 *
 * One constant, one edit, and no prose moves either way — which is the reason it
 * is a constant rather than a branch spread over two panels.
 */
const ERA_HEADING: "name" | "title" | "both" = "both";

/** `?book=0` — the start flow, off. Default **on**: the book is how a session opens. */
const BOOK_PARAM = "book";

/**
 * The book as pages, one per age, oldest first.
 *
 * **The one assembly, used by both readings** — the session opening on the book
 * and the copy standing on a town's shelf (`hud/opening.ts`,
 * `witness/witness.ts`). O6's exit criterion is that the *same text* is readable
 * in-world, and one function producing both pages is what makes that a fact
 * rather than a promise about two call sites.
 *
 * Pure, and that is what a self-check asks its questions of: every string on a
 * page comes off a {@link SagaEra} and can be compared with what the source
 * answered (`dev/selfcheck.ts`'s `checkSaga`).
 */
export function erasPages(book: SagaBook): readonly ErasPage[] {
  return book.eras.map((era) => {
    const { heading, under } = eraHeading(era);

    return {
      heading,
      under,
      lines: era.lines.map((line) => line.text),
    } satisfies ErasPage;
  });
}

/**
 * Returns the heading a page leads with, and the line under it.
 *
 * Two strings out of the simulation's two, chosen by {@link ERA_HEADING} and
 * never composed: an age whose boundary gave it only one of them shows that one
 * on its own rather than a heading and an empty line under it.
 */
function eraHeading(era: SagaEra): { readonly heading: string; readonly under: string } {
  if (ERA_HEADING === "name") {
    return { heading: era.name === "" ? era.title : era.name, under: "" };
  }

  if (ERA_HEADING === "title") {
    return { heading: era.title === "" ? era.name : era.title, under: "" };
  }

  if (era.name === "" || era.title === "") {
    return { heading: era.name === "" ? era.title : era.name, under: "" };
  }

  return { heading: era.name, under: era.title };
}

/**
 * Whether this session opens on the Book of Eras: `?book=0` says it does not.
 *
 * **Default on, and gated on the URL alone** — never on `import.meta.env.DEV`,
 * which is O7's rule and the trap it names: the owner playtests the *deployed*
 * preview, which is a production build, so a start flow that only existed in
 * development would be a start flow nobody ever judged.
 *
 * Night-start against book-start is the open taste call this parameter exists
 * for (plan, finalize section): the book is what a bare link opens on, and
 * `?book=0` is the same session without it — one playtest, both, and the owner
 * says which.
 *
 * It lives here rather than in `session/params.ts` for the reason `?seed`,
 * `?bootstrap` and `?fixture` live where they are read: a parameter belongs to
 * whatever it turns on. The spelling is that file's — absent or anything but an
 * explicit off is on — with the default inverted, because the book is the
 * shipped behaviour and the parameter is how somebody declines it.
 */
export function bookRequested(search: string): boolean {
  const raw = new URLSearchParams(search).get(BOOK_PARAM);

  if (raw === null) {
    return true;
  }

  const value = raw.trim().toLowerCase();

  return value !== "0" && value !== "false" && value !== "off";
}

/**
 * Turns the salience-decay knob on, if this link asked for it and this build can.
 *
 * Decision 7's client half, through O7's rule: **the knob is a URL parameter and
 * nothing else**, so the owner can flip it in the deployed preview rather than
 * in a development session nobody plays. Default off — the sim ships it off, and
 * whether it becomes the default is what the playtest decides.
 *
 * Applied once, at boot, because it is a property of the *reading* rather than
 * of the world: decay lives in the projections, so nothing about the replay, the
 * hashes or the log moves when it is on (`Snapshot::with_salience_decay`).
 *
 * Returns the one line the HUD should say — what was turned on, or why it was
 * not — and `null` when the link asked for nothing at all. Saying it is the rule
 * `?home=` set: a session quietly running different pacing than the link named
 * is an unreproducible playtest, and the days it is running on are exactly what
 * the owner is judging.
 *
 * `fades` is the caller's answer to whether this session's world has anything to
 * fade, and it exists for one source: the hand-authored fixture takes the call
 * and does nothing with it, because every line in it is a string somebody typed
 * and no projection is standing behind one (`world/fixture.ts`). The knob is
 * still turned — the seam is the same shape either way — and the sentence says
 * where it landed, which is the same honesty the clause above is about.
 */
export function applySalienceDecay(
  source: WitnessSource,
  search: string,
  fades: boolean,
): string | null {
  const days = decayRequested(search);

  if (days === null) {
    return null;
  }

  const knob = (source as Partial<WitnessSource>).setSalienceDecay;

  if (typeof knob !== "function") {
    return `?decay=${days} asked for salience decay — this build's world has no such knob`;
  }

  knob.call(source, days);

  if (!fades) {
    return `?decay=${days} asked for salience decay — this session's world is hand-authored and has nothing that fades`;
  }

  return `salience decay on — what a town holds fades by half every ${days} days`;
}

/**
 * Reads the whole book, refusing any line that cites nothing.
 *
 * The one call across the saga seam, and it does two things the boundary's own
 * declaration cannot do for it. The export is **presence-checked**, so a build
 * whose boundary has not grown it opens a session rather than throwing (see the
 * header). And the citation rule is enforced: a line with no record behind it is
 * dropped and counted, because a page of prose the log cannot account for is the
 * exact failure O6 exists to prevent. An age left with no readable line at all is
 * not a page either — an empty chapter is chrome about nothing.
 */
export function readSaga(source: WitnessSource): SagaBook {
  const asked = (source as Partial<WitnessSource>).saga;

  if (typeof asked !== "function") {
    return EMPTY_SAGA;
  }

  const eras: SagaEra[] = [];
  let refused = 0;

  for (const era of asked.call(source)) {
    const lines = era.lines.filter((line) => line.text !== "" && line.cites.length > 0);

    refused += era.lines.length - lines.length;

    if (lines.length === 0) {
      continue;
    }

    // The age exactly as the boundary said it, with the refusals taken out: the
    // ordinal, the two names and the days are the simulation's and nothing here
    // rewrites one.
    eras.push({ ...era, lines });
  }

  return { eras, refused };
}
